import React, { useEffect, useMemo, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDepartments } from "@/contexts/DepartmentsContext";
import DepartmentChips, { DepartmentBadge } from "@/components/DepartmentChips";
import AssetFormDialog from "@/components/AssetFormDialog";
import PersonFormDialog from "@/components/PersonFormDialog";
import ManageAssetCategoriesDialog from "@/components/ManageAssetCategoriesDialog";
import ExportButtons from "@/components/ExportButtons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Edit3,
  Search,
  Settings2,
  ChevronDown,
  Layers,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Wrench,
  PackageX,
  Printer,
  Trash2,
  LayoutGrid,
  Table as TableIcon,
  ClipboardList,
  Camera,
  MapPin,
  Copy,
  Tag,
  Cog,
  Boxes,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import QuickViewAssets from "@/components/QuickViewAssets";
import GroupedAssetsView from "@/components/GroupedAssetsView";

const STATUS_META = {
  available: { label: "Available", icon: CheckCircle2, cls: "bg-[#E6F4EA] text-[#1F6F3E]" },
  in_use: { label: "In use", icon: CheckCircle2, cls: "bg-[#E7EEFA] text-[#1F4A99]" },
  broken_unconfirmed: { label: "Broken (unconfirmed)", icon: AlertCircle, cls: "bg-[#FFF7DB] text-[#5B4500]" },
  broken: { label: "Broken", icon: Wrench, cls: "bg-[#FCE4E4] text-[#8A1F1F]" },
  retired: { label: "Retired", icon: PackageX, cls: "bg-[#EEE] text-[#555]" },
  lost: { label: "Lost", icon: XCircle, cls: "bg-[#FCE4E4] text-[#8A1F1F]" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.available;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`asset-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full border border-black/30 ${meta.cls}`}
    >
      <Icon size={10} strokeWidth={2.5} /> {meta.label}
    </span>
  );
};

const Assets = () => {
  const { user, isManager } = useAuth();
  const { departments } = useDepartments();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  // v186 — inline "add new person" from the asset dialog. Persists the
  // caller's callback so we can auto-select the newly created person on
  // the asset assignee dropdown once they save.
  const [personDlgOpen, setPersonDlgOpen] = useState(false);
  const [personDlgOnSaved, setPersonDlgOnSaved] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dept, setDept] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  // Persist Cards ↔ Quick View toggle so managers keep the table open during
  // long fill-in sessions across page reloads.
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("assets-view-mode") || "cards",
  );
  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("assets-view-mode", mode);
  };
  // Card layout: individual (one card per row) or grouped (near-identical
  // items collapsed into a single stack card with a "×N" badge). Grouping
  // is the go-to view once bulk imports have created dozens of identical
  // monitors/keyboards/chairs.
  const [groupMode, setGroupMode] = useState(
    () => localStorage.getItem("assets-group-mode") || "individual",
  );
  const switchGroup = (mode) => {
    setGroupMode(mode);
    localStorage.setItem("assets-group-mode", mode);
  };

  const load = async () => {
    const [{ data: a }, { data: c }, { data: u }, { data: p }] = await Promise.all([
      api.get("/assets"),
      api.get("/asset-categories"),
      api.get("/users/public"),
      api.get("/people").catch(() => ({ data: [] })),
    ]);
    setAssets(a || []);
    setCategories(c || []);
    setUsers(u || []);
    setPeople(p || []);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (category !== "all" && (a.category || "") !== category) return false;
      if (dept !== "all" && (a.department || "") !== dept) return false;
      if (statusFilter !== "all" && (a.status || "available") !== statusFilter) return false;
      if (locationFilter !== "all" && (a.location || "") !== locationFilter) return false;
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.serial || "").toLowerCase().includes(q) ||
        (a.assignee_text || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    });
  }, [assets, search, category, dept, statusFilter, locationFilter]);

  const assigneeLabel = (a) => {
    if (a.assignee_kind === "user") {
      const u = users.find((x) => x.id === a.assignee_id);
      return u ? u.name : "—";
    }
    if (a.assignee_kind === "person") {
      const p = people.find((x) => x.id === a.assignee_id);
      if (!p) return "—";
      return p.active === false ? `${p.name} (inactive)` : p.name;
    }
    if (a.assignee_kind === "dept") {
      const d = departments.find((x) => x.id === a.assignee_id);
      return d ? `${d.name} (dept)` : "—";
    }
    if (a.assignee_kind === "text") return a.assignee_text || "—";
    return "Unassigned";
  };

  const warrantyHint = (a) => {
    if (!a.warranty_until) return null;
    const due = new Date(a.warranty_until);
    const now = new Date();
    const days = Math.round((due - now) / 86400000);
    if (days < 0) return { label: `Warranty expired ${-days}d ago`, danger: true };
    if (days < 60) return { label: `Warranty in ${days}d`, warn: true };
    return { label: due.toLocaleDateString(), ok: true };
  };

  const flagBroken = async (a) => {
    try {
      await api.put(`/assets/${a.id}`, { status: "broken_unconfirmed" });
      toast.success(`Flagged "${a.name}" as broken (awaiting confirmation)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not flag");
    }
  };
  const confirmBroken = async (a) => {
    try {
      await api.put(`/assets/${a.id}`, { status: "broken" });
      toast.success("Confirmed broken");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    }
  };
  const retire = async (a) => {
    if (!window.confirm(`Retire "${a.name}"?`)) return;
    try {
      await api.put(`/assets/${a.id}`, { status: "retired" });
      toast.success("Retired");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not retire");
    }
  };
  const deleteAsset = async (a) => {
    if (
      !window.confirm(
        `Delete "${a.name}" permanently?\n\nThis removes the asset record from the database. History and any linked references will be lost.\n\nIf you just want to stop tracking it day-to-day, use Retire instead.`,
      )
    )
      return;
    try {
      await api.delete(`/assets/${a.id}`);
      toast.success(`Deleted "${a.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  // Duplicate — clone the asset's descriptive fields (brand/model/category
  // /department/location/cost/etc.) but drop serial (must be unique per
  // physical unit) and photos (they belong to the original). Name is
  // suffixed with " (copy)". After the POST the edit dialog opens on the
  // new record so the manager can immediately fix the serial or reassign.
  const duplicate = async (a) => {
    try {
      const body = {
        name: `${a.name} (copy)`,
        category: a.category || null,
        cost: a.cost || null,
        purchased_at: a.purchased_at || null,
        warranty_until: a.warranty_until || null,
        department: a.department || null,
        status: "available",
        notes: a.notes || null,
        location: a.location || null,
        brand: a.brand || null,
        model: a.model || null,
        condition: a.condition || null,
        next_inspection: a.next_inspection || null,
        po_ref: a.po_ref || null,
        // Keep assignee intact — many duplicates go to the same person
        // (spare monitor / keyboard). Manager can override in the dialog.
        assignee_kind: a.assignee_kind || null,
        assignee_id: a.assignee_id || null,
        assignee_text: a.assignee_text || null,
      };
      const { data: created } = await api.post("/assets", body);
      toast.success(`Duplicated "${a.name}" — set a new serial next`);
      await load();
      setEditing(created);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not duplicate");
    }
  };

  // Move — convert this asset into a different register (machinery /
  // inventory). Preserves brand→manufacturer, category, cost, department,
  // notes and photos; drops asset-only fields (assignee, condition, etc.).
  // The source record is deleted server-side, so we reload the grid after.
  const moveTo = async (a, targetKind) => {
    const label = targetKind === "machine" ? "Machinery" : "Inventory";
    if (
      !window.confirm(
        `Move "${a.name}" into ${label}?\n\nThis will:\n • create a new record in the ${label} register with the same brand/model/serial/cost/photos\n • delete the current Asset record\n\nHistory linked to this asset ID will not be moved.`,
      )
    )
      return;
    try {
      await api.post("/records/move", {
        source_kind: "asset",
        source_id: a.id,
        target_kind: targetKind,
      });
      toast.success(`Moved "${a.name}" to ${label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move record");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="assets-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Inventory</div>
          <h1 className="font-display text-4xl">Assets</h1>
          <p className="text-sm text-[#607087] mt-1">
            Discrete, serial-tracked items — laptops, scanners, ladders, trolleys.
            <span className="block text-xs text-[#8B96A6] mt-0.5">
              Use Assets when you mostly care <b>who has it &amp; where it lives</b>. If it needs scheduled servicing (oil, blades, ink), add it in <b>Machinery</b> instead — don&apos;t enter in both.
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/assets-add-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="assets-print-add-btn"
            className="nb-btn bg-white px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold"
            title="Print blank forms staff can fill in for assets found around the departments that aren't in the register yet"
          >
            <Printer size={14} strokeWidth={2.5} /> Print add-asset form
          </a>
          <a
            href="/walkaround-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="assets-walkaround-print-btn"
            className="nb-btn bg-[#FFF3D4] px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold border-[#7A5A00]"
            title="Print a walk-around capture sheet — 15 rows on one page, includes Qty column for identical items (e.g. 8 boardroom chairs → 1 row Qty=8)"
          >
            <ClipboardList size={14} strokeWidth={2.5} /> Walk-around sheet
          </a>
          <a
            href="/walkaround-import"
            data-testid="assets-walkaround-import-btn"
            className="nb-btn bg-[#F1E7FA] px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold border-[#5B3A99]"
            title="Snap a filled walk-around sheet — the app reads it and lets you review before adding to the register"
          >
            <Camera size={14} strokeWidth={2.5} /> Snap-to-Import
          </a>
          <a
            href="/workstation-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="assets-workstation-print-btn"
            className="nb-btn bg-[#E6F4EA] px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold border-[#1F6F3E]"
            title="Print a one-page snapshot per staff member — every asset on their desk, with tick boxes for walk-around audits"
          >
            <UserCheck size={14} strokeWidth={2.5} /> Workstation snapshots
          </a>
          <a
            href="/it-station-add-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="assets-print-it-station-btn"
            className="nb-btn bg-white px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold"
            title="Print blank forms for staff to walk their desks and log every bit of IT kit at each workstation"
          >
            <Printer size={14} strokeWidth={2.5} /> Print IT station audit
          </a>
          <ExportButtons
            entity="assets"
            rows={assets}
            testidPrefix="assets-export"
          />
          {isManager && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="assets-manage-lists-btn"
                  className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
                >
                  <Settings2 size={18} strokeWidth={2.5} /> Manage lists
                  <ChevronDown size={14} strokeWidth={2.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="border-2 border-black rounded-xl shadow-[4px_4px_0_#001740] bg-white min-w-[14rem] p-1.5"
              >
                <DropdownMenuItem
                  data-testid="assets-manage-categories-btn"
                  onClick={() => setManageCatsOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
                >
                  <Layers size={16} strokeWidth={2.5} /> Asset categories
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isManager && (
            <button
              data-testid="assets-add-btn"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              className="nb-btn nb-btn-primary px-4 py-4 flex items-center gap-2"
            >
              <Plus size={18} strokeWidth={2.5} /> Add asset
            </button>
          )}
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={18}
          strokeWidth={2.5}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#001740]/60"
        />
        <input
          data-testid="assets-search-input"
          className="nb-input pl-10"
          placeholder="Search name, serial, assigned-to…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <DepartmentChips value={dept} onChange={setDept} testidPrefix="assets-dept" />
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="assets-cat-chips">
          {categories.map((c) => {
            const active = category === c.name;
            return (
              <button
                key={c.id}
                data-testid={`assets-cat-${c.name}`}
                onClick={() => setCategory(active ? "all" : c.name)}
                className={`nb-btn px-3 py-1.5 text-sm transition-all ${
                  active ? "nb-btn-dark" : "bg-white"
                }`}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Location chip row — derived from what's actually in the register,
       * so managers can pull "everything in Boardroom" after a walk-around.
       * Only shows if we have >1 distinct location to switch between. */}
      {(() => {
        const locs = Array.from(
          new Set(assets.map((a) => (a.location || "").trim()).filter(Boolean)),
        ).sort();
        if (locs.length < 2) return null;
        return (
          <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="assets-location-chips">
            <span className="label-eyebrow text-[#607087] flex items-center gap-1 mr-1">
              <MapPin size={12} strokeWidth={2.5} /> Location
            </span>
            <button
              data-testid="assets-location-all"
              onClick={() => setLocationFilter("all")}
              className={`nb-btn px-3 py-1.5 text-xs transition-all ${
                locationFilter === "all" ? "nb-btn-dark" : "bg-white"
              }`}
            >
              All
            </button>
            {locs.map((loc) => {
              const active = locationFilter === loc;
              return (
                <button
                  key={loc}
                  data-testid={`assets-location-${loc}`}
                  onClick={() => setLocationFilter(active ? "all" : loc)}
                  className={`nb-btn px-3 py-1.5 text-xs transition-all ${
                    active ? "nb-btn-dark" : "bg-white"
                  }`}
                >
                  {loc}
                </button>
              );
            })}
          </div>
        );
      })()}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="assets-status-chips">
        {["all", "available", "in_use", "broken_unconfirmed", "broken", "retired"].map((s) => {
          const active = statusFilter === s;
          const label = s === "all" ? "All statuses" : STATUS_META[s]?.label || s;
          return (
            <button
              key={s}
              data-testid={`assets-status-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`nb-btn px-3 py-1.5 text-xs transition-all ${
                active ? "nb-btn-dark" : "bg-white"
              }`}
            >
              {label}
            </button>
          );
        })}
        <div
          className="nb-card p-0.5 flex items-center gap-0.5 ml-auto"
          data-testid="assets-view-toggle"
          role="group"
          aria-label="View mode"
        >
          <button
            data-testid="assets-view-cards"
            onClick={() => switchView("cards")}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
              viewMode === "cards"
                ? "bg-[#001740] text-white"
                : "text-[#001740] hover:bg-[#F4F8FB]"
            }`}
          >
            <LayoutGrid size={12} strokeWidth={2.5} /> Cards
          </button>
          <button
            data-testid="assets-view-table"
            onClick={() => switchView("table")}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
              viewMode === "table"
                ? "bg-[#001740] text-white"
                : "text-[#001740] hover:bg-[#F4F8FB]"
            }`}
          >
            <TableIcon size={12} strokeWidth={2.5} /> Quick View
          </button>
        </div>
        {viewMode === "cards" && (
          <div
            className="nb-card p-0.5 flex items-center gap-0.5"
            data-testid="assets-group-toggle"
            role="group"
            aria-label="Group mode"
            title="Group near-identical items (e.g. 47 Dell monitors → one stack card)"
          >
            <button
              data-testid="assets-group-individual"
              onClick={() => switchGroup("individual")}
              className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
                groupMode === "individual"
                  ? "bg-[#001740] text-white"
                  : "text-[#001740] hover:bg-[#F4F8FB]"
              }`}
            >
              <Layers size={12} strokeWidth={2.5} /> Every unit
            </button>
            <button
              data-testid="assets-group-grouped"
              onClick={() => switchGroup("grouped")}
              className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
                groupMode === "grouped"
                  ? "bg-[#001740] text-white"
                  : "text-[#001740] hover:bg-[#F4F8FB]"
              }`}
            >
              <Layers size={12} strokeWidth={2.5} /> Stacked
            </button>
          </div>
        )}
      </div>

      {viewMode === "table" ? (
        <QuickViewAssets
          items={filtered}
          users={users}
          categories={categories}
          onSaved={load}
        />
      ) : groupMode === "grouped" ? (
        <GroupedAssetsView
          items={filtered}
          isManager={isManager}
          onEdit={(a) => {
            setEditing(a);
            setOpen(true);
          }}
          onDelete={deleteAsset}
        />
      ) : filtered.length === 0 ? (
        <div className="nb-card p-8 text-center text-sm text-[#607087]" data-testid="assets-empty">
          No assets match your filters. {isManager && "Click + Add asset to start tracking one."}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((a) => {
            const warr = warrantyHint(a);
            return (
              <div
                key={a.id}
                data-testid={`asset-card-${a.id}`}
                className="nb-card p-4 flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  {a.image_paths?.[0] ? (
                    <img
                      src={imageUrl(a.image_paths[0])}
                      alt=""
                      className="w-20 h-20 rounded-lg border-2 border-black object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-black bg-[#F4F8FB] shrink-0 flex items-center justify-center text-[#001740]/30">
                      <Layers size={28} strokeWidth={2} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight truncate">{a.name}</div>
                    {(a.brand || a.model) && (
                      <div
                        data-testid={`asset-brand-${a.id}`}
                        className="text-xs font-bold text-[#001740] truncate mt-0.5 flex items-center gap-1"
                      >
                        <Tag size={11} strokeWidth={2.5} />
                        {[a.brand, a.model].filter(Boolean).join(" ")}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      <StatusBadge status={a.status || "available"} />
                      {a.category && (
                        <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]">
                          {a.category}
                        </span>
                      )}
                      <DepartmentBadge department={a.department} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {a.serial && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Serial</div>
                      <div className="font-mono-num truncate">{a.serial}</div>
                    </div>
                  )}
                  <div>
                    <div className="label-eyebrow text-[#607087]">Assigned to</div>
                    <div className="truncate">{assigneeLabel(a)}</div>
                  </div>
                  {a.cost && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Cost</div>
                      <div className="font-mono-num">${Number(a.cost).toFixed(2)}</div>
                    </div>
                  )}
                  {warr && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Warranty</div>
                      <div
                        className={`font-semibold ${
                          warr.danger
                            ? "text-[#8A1F1F]"
                            : warr.warn
                            ? "text-[#7A5500]"
                            : ""
                        }`}
                      >
                        {warr.label}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-black/10">
                  {isManager && (
                    <button
                      data-testid={`asset-edit-${a.id}`}
                      onClick={() => {
                        setEditing(a);
                        setOpen(true);
                      }}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <Edit3 size={12} strokeWidth={2.5} /> Edit
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`asset-duplicate-${a.id}`}
                      onClick={() => duplicate(a)}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Clone this asset — same brand/model/category, blank serial, ready to edit"
                    >
                      <Copy size={12} strokeWidth={2.5} /> Duplicate
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`asset-move-machine-${a.id}`}
                      onClick={() => moveTo(a, "machine")}
                      className="nb-btn bg-[#FFF7D6] px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Convert this record into Machinery (keeps brand/model/serial/cost/photos)"
                    >
                      <Cog size={12} strokeWidth={2.5} /> → Machinery
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`asset-move-stock-${a.id}`}
                      onClick={() => moveTo(a, "stock")}
                      className="nb-btn bg-[#FFF7D6] px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Convert this record into Inventory (turns into a stock line)"
                    >
                      <Boxes size={12} strokeWidth={2.5} /> → Inventory
                    </button>
                  )}
                  {a.status !== "broken_unconfirmed" &&
                    a.status !== "broken" &&
                    a.status !== "retired" && (
                      <button
                        data-testid={`asset-flag-broken-${a.id}`}
                        onClick={() => flagBroken(a)}
                        className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                      >
                        <AlertCircle size={12} strokeWidth={2.5} /> Mark broken
                      </button>
                    )}
                  {a.status === "broken_unconfirmed" && isManager && (
                    <button
                      data-testid={`asset-confirm-broken-${a.id}`}
                      onClick={() => confirmBroken(a)}
                      className="nb-btn nb-btn-danger px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <Wrench size={12} strokeWidth={2.5} /> Confirm broken
                    </button>
                  )}
                  {isManager && a.status !== "retired" && (
                    <button
                      data-testid={`asset-retire-${a.id}`}
                      onClick={() => retire(a)}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <PackageX size={12} strokeWidth={2.5} /> Retire
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`asset-delete-${a.id}`}
                      onClick={() => deleteAsset(a)}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1 text-[#8A1F1F] border-[#8A1F1F]/40 hover:bg-[#FCE4E4]"
                      title="Permanently delete this asset (cannot be undone)"
                    >
                      <Trash2 size={12} strokeWidth={2.5} /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AssetFormDialog
        open={open}
        onClose={() => setOpen(false)}
        editing={editing}
        categories={categories}
        users={users}
        departments={departments}
        people={people}
        onSaved={load}
        onCategoriesChanged={load}
        onAddPerson={(onSavedCb) => {
          setPersonDlgOnSaved(() => onSavedCb);
          setPersonDlgOpen(true);
        }}
      />
      <PersonFormDialog
        open={personDlgOpen}
        person={null}
        onClose={() => setPersonDlgOpen(false)}
        onSaved={async () => {
          const { data } = await api.get("/people").catch(() => ({ data: [] }));
          setPeople(data || []);
          // Auto-select the newest person (highest created_at) on the
          // assignee dropdown of the still-open asset dialog.
          if (personDlgOnSaved && data?.length) {
            const newest = [...data].sort((a, b) =>
              (b.created_at || "").localeCompare(a.created_at || ""),
            )[0];
            personDlgOnSaved(newest);
          }
        }}
      />
      <ManageAssetCategoriesDialog
        open={manageCatsOpen}
        onClose={() => setManageCatsOpen(false)}
        onChanged={load}
      />
    </div>
  );
};

export default Assets;
